import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { StatCard, Card, Badge, PageHeader } from '../../components/UI';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

export default function DashHome() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [profile, setProfile] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/users/profile'),
      api.get('/api/analytics'),
      api.get('/api/contacts/followups/today'),
      api.get('/api/announcements'),
    ]).then(([p, a, f, an]) => {
      if (p.data.success) setProfile(p.data.user);
      if (a.data.success) setAnalytics(a.data);
      if (f.data.success) setFollowups(f.data.followups);
      if (an.data.success) setAnnouncements(an.data.announcements);
    }).finally(() => setLoading(false));
  }, []);

  const daysLeft = profile?.plan_expiry_date
    ? Math.ceil((new Date(profile.plan_expiry_date) - new Date()) / 86400000)
    : null;

  return (
    <div>
      <PageHeader title="Dashboard" />

      {/* Warnings */}
      {profile?.status === 'suspended' && (
        <div style={{ background: 'rgba(255,68,68,0.08)', border: '1px solid rgba(255,68,68,0.25)', borderRadius: 10, padding: 16, marginBottom: 20, fontSize: 14, color: '#ff6b6b' }}>
          ⚠️ Your account is <strong>suspended</strong>. Please <Link to="/dashboard/billing" style={{ color: '#ff6b6b', fontWeight: 700, textDecoration: 'underline' }}>renew your plan</Link> to restore access.
        </div>
      )}
      {daysLeft !== null && daysLeft <= 7 && daysLeft > 0 && (
        <div style={{ background: 'rgba(255,215,0,0.06)', border: '1px solid rgba(255,215,0,0.2)', borderRadius: 10, padding: 16, marginBottom: 20, fontSize: 14, color: '#ffd700' }}>
          ⏰ Your plan expires in <strong>{daysLeft} days</strong>. <Link to="/dashboard/billing" style={{ color: '#ffd700', fontWeight: 700, textDecoration: 'underline' }}>Renew now →</Link>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard icon="📱" value={analytics?.messages?.today?.toLocaleString() ?? '—'} label="Messages Today" />
        <StatCard icon="👥" value={analytics?.contacts?.total?.toLocaleString() ?? '—'} label="Total Contacts" />
        <StatCard icon="📤" value={analytics?.campaigns?.total ?? '—'} label="Campaigns" />
        <StatCard icon="🔌" value={analytics?.widget?.leads_month ?? '—'} label="Widget Leads" />
        <StatCard icon="🔴" value={analytics?.contacts?.hot_leads ?? '—'} label="Hot Leads" color="#ff6b6b" />
        <StatCard icon="✅" value={analytics?.contacts?.closed ?? '—'} label="Closed Deals" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 20 }}>
        {/* Plan Info */}
        <Card title="📋 My Plan">
          {profile ? (
            <>
              {[
                ['Plan', <span style={{ background: 'var(--green-dim)', color: 'var(--green)', padding: '3px 10px', borderRadius: 100, fontSize: 12, fontWeight: 700 }}>{profile.plan_name || 'No Plan'}</span>],
                ['Status', <Badge>{profile.status}</Badge>],
                ['Billing', profile.billing_cycle || '—'],
                ['Expires', profile.plan_expiry_date ? new Date(profile.plan_expiry_date).toLocaleDateString('en-IN') : '—'],
                ['Currency', profile.currency || 'INR'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)' }}>{k}</span>
                  <span>{v}</span>
                </div>
              ))}
              <Link to="/dashboard/billing" style={{ display: 'block', textAlign: 'center', marginTop: 16, padding: '10px', borderRadius: 8, background: 'var(--green)', color: '#000', fontFamily: 'Sora', fontWeight: 600, fontSize: 13 }}>
                Manage Billing →
              </Link>
            </>
          ) : <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>Loading...</div>}
        </Card>

        {/* Follow Ups */}
        <Card title="🔔 Today's Follow Ups">
          {followups.length > 0 ? followups.map(f => (
            <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span style={{ fontWeight: 500 }}>{f.name}</span>
              <span style={{ color: '#ffd700', fontSize: 12 }}>{f.whatsapp}</span>
            </div>
          )) : (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px 0', fontSize: 13 }}>No follow ups today 🎉</div>
          )}
        </Card>

        {/* Announcements */}
        <Card title="📣 Announcements">
          {announcements.length > 0 ? announcements.map(a => (
            <div key={a.id} style={{ background: 'rgba(0,231,76,0.04)', border: '1px solid rgba(0,231,76,0.1)', borderRadius: 8, padding: 12, marginBottom: 8 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{a.title}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{a.message}</div>
            </div>
          )) : (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px 0', fontSize: 13 }}>No announcements</div>
          )}
        </Card>

        {/* Quick Actions */}
        <Card title="⚡ Quick Actions">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              ['/dashboard/crm', '➕ Add Contact'],
              ['/dashboard/campaigns', '📤 New Campaign'],
              ['/dashboard/whatsapp', '📱 Connect WA'],
              ['/dashboard/widget', '🔌 Widget Setup'],
              ['/dashboard/tickets', '🎫 Support'],
              ['/dashboard/billing', '💳 Billing'],
            ].map(([path, label]) => (
              <Link key={path} to={path} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'var(--bg)', border: '1px solid var(--border)',
                color: 'var(--text-secondary)', padding: 12, borderRadius: 8,
                fontSize: 12, fontWeight: 500, textDecoration: 'none', transition: 'all 0.2s'
              }}
                onMouseOver={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.color = 'var(--green)'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
              >{label}</Link>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
